<template>
  <v-container fluid class="pa-0">
    <EntityIndex
        :loading="loading"
        :total-count="totalCount"
        :table-headers="tableHeaders"
        :table-rows="tableRows"
        :table-options.sync="tableOptions"
        :selected-rows.sync="selectedRows"
        :selected-rows-actions="selectedRowsActions"
        :no-data-text="$t('siteNotification.noSiteNotifications')"
        item-key="siteNotification.id">

      <template v-slot:actions>
        <v-btn v-if="$featurePermissions.siteNotificationCRUD"
               color="primary"
               elevation="2"
               class="mb-2"
               @click="$refs.siteNotificationDialog.openCreateNewSiteNotification()">
          <v-icon>mdi-plus</v-icon>
          {{ $t('actions.add') }}
        </v-btn>
      </template>

      <template v-slot:item.title="{ item }">
        <router-link :to="item.routerLinkTo">{{ item.siteNotification.title }}</router-link>
      </template>


      <template v-slot:entityListItem="props">
        <site-notification-list-item
            :item="props.item"
            :show-actions="true"
            :row-click-enabled="true"/>
      </template>

    </EntityIndex>


    <entity-confirm-delete-dialog
        ref="confirmDeleteDialog"
        :text="$t('siteNotification.deleteConfirm')"
        :text-plural="$t('siteNotification.deleteConfirmPlural')"
        @confirmedDelete="deleteSiteNotifications">
      <template v-slot:entityListItem="props">
        <site-notification-list-item :item="props.item"/>
      </template>
    </entity-confirm-delete-dialog>

    <site-notification-dialog ref="siteNotificationDialog" @done="siteNotificationCreatedUpdatedEdited"/>
  </v-container>

</template>

<style scoped>
</style>

<script>
import i18n from '@/i18n';
import EntityIndex from '@/components/entity-crud/EntityIndex';
import EntityConfirmDeleteDialog from '@/components/entity-crud/EntityDeleteConfirmDialog';
import SiteNotificationsService from '@/services/SiteNotificationsService';
import SiteNotificationListItem from '@/views/sitenotification/SiteNotificationListItem';
import SiteNotificationDialog from '@/views/sitenotification/SiteNotificationDialog';

export default {
  components: {
    SiteNotificationDialog,
    SiteNotificationListItem,
    EntityConfirmDeleteDialog,
    EntityIndex,
  },
  name: 'SiteNotificationIndex',
  data() {
    return {
      siteNotifications: [],
      selectedRows: [],
      loading: false,
      totalCount: 0,
    };
  },
  computed: {

    tableOptions: {
      get() {
        return this.$store.state.siteNotificationIndexState.tableOptions;
      },
      set(value) {
        this.$store.commit('siteNotificationIndexState/setTableOptions', value);
      },
    },

    tableHeaders() {
      return [
        {
          value: 'type',
          text: i18n.t('siteNotification.type'),
          getValue: siteNotification => {
            return this.$options.filters.siteNotificationType(siteNotification.type);
          },
        },
        {
          value: 'title',
          text: i18n.t('siteNotification.title'),
          getValue: siteNotification => {
            return siteNotification.title;
          },
        },
        {
          value: 'startTime',
          text: i18n.t('siteNotification.startTime'),
          getValue: siteNotification => {
            return this.$options.filters.date(siteNotification.startTime);
          },
        },
        {
          value: 'endTime',
          text: i18n.t('siteNotification.endTime'),
          getValue: siteNotification => {
            return this.$options.filters.date(siteNotification.endTime);
          },
        },
        {
          value: 'rowActions',
          text: '',
          getValue: siteNotification => {
            return this.createRowActions(siteNotification);
          },
        },
      ];
    },
    tableRows() {
      return this.siteNotifications.map(siteNotification => {
        let row = {};
        row.siteNotification = siteNotification;
        row.routerLinkTo = `sitenotification/${siteNotification.id}`;

        row.rowClick = () => {
          this.$router.push(row.routerLinkTo);
        };

        this.tableHeaders.forEach(header => {
          if (header.getValue) {
            row[header.value] = header.getValue(siteNotification);
          }
        });
        return row;
      });
    },

    selectedRowsActions() {
      const actions = [
        {
          label: i18n.t('actions.delete'),
          icon: 'mdi-delete',
          disabled: this.selectedRows.length === 0,
          hasPermission: this.$featurePermissions.siteNotificationCRUD,
          click: () => {
            this.confirmDeleteSiteNotifications(this.selectedRows);
          },
        },
      ];
      return actions.filter(action => action.hasPermission);
    },
  },
  methods: {
    createRowActions(siteNotification) {

      let actions = [
        {
          label: i18n.t('actions.show'),
          icon: 'mdi-view-list',
          hasPermission: this.$featurePermissions.siteNotificationCRUD,
          click: () => {
            this.$router.push(`sitenotification/${siteNotification.id}`);
          },
        },
        {
          label: i18n.t('actions.modify'),
          icon: 'mdi-pencil-outline',
          hasPermission: this.$featurePermissions.siteNotificationCRUD,
          click: () => {
            this.openEditDialog(siteNotification.id);
          },
        },
        {
          label: i18n.t('actions.delete'),
          icon: 'mdi-delete',
          dividerBefore: true,
          hasPermission: this.$featurePermissions.siteNotificationCRUD,
          click: () => {
            this.confirmDeleteSiteNotifications([{ siteNotification: siteNotification }]);
          },
        },
      ];
      return actions.filter(action => action.hasPermission);
    },

    confirmDeleteSiteNotifications(items) {
      this.$refs.confirmDeleteDialog.show(items);
    },
    deleteSiteNotifications(items) {
      const siteNotificationIds = items.map(item => item.siteNotification.id);
      SiteNotificationsService.delete(siteNotificationIds)
          .finally(() => {
            this.$refs.confirmDeleteDialog.close();
            this.siteNotificationCreatedUpdatedEdited();
          });
    },

    openEditDialog(siteNotificationId) {
      this.$refs.siteNotificationDialog.openEditForSiteNotification(siteNotificationId);
    },

    fetch() {
      this.loading = true;
      this.selectedRows = [];
      SiteNotificationsService.list({
            limit: this.tableOptions.itemsPerPage,
            offset: (this.tableOptions.page - 1) * this.tableOptions.itemsPerPage,
            orderBy: this.tableOptions.sortBy[0],
            order: this.tableOptions.sortDesc[0] ? 'DESC' : 'ASC',
          },
      ).then(result => {
        this.loading = false;
        this.siteNotifications = result.data.items;
        this.totalCount = result.data.totalCount;
      }).catch(result => {
        console.log(JSON.stringify(result));
        this.loading = false;
      });
    },

    siteNotificationCreatedUpdatedEdited(){
      this.fetch();
      this.$store.dispatch('siteNotificationsViewerState/update');
    },
  },


  watch: {
    tableOptions: {
      handler() {
        this.fetch();
      },
      deep: true,
    },
  },
  created() {
    this.fetch();
  },
};
</script>
